import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { CommandsCellRenderer } from '@/App/Blotter/grids/cash/CommandsCellRenderer';
import {
  AmountRenderer,
  DateRenderer,
  PointsRenderer,
  RateRenderer,
  StatusCellRenderer,
} from '@/App/Blotter/grids/commonCellRenderers.tsx';
import { basicTimeFormat } from '@/App/Blotter/grids/commonFormatters.ts';
import {
  dateFilterDefinition,
  numberFilterDefinition,
  setFilterDefinition,
  textFilterDefinition,
} from '@/App/Blotter/grids/filterDefinitions.ts';
import type { BlotterCashEntry } from '@/store/blotter/types/BlotterCashEntry.ts';
import type { ISODateTime } from '@/types/date.ts';

export function useColumnDefinitions() {
  const { formatMessage } = useIntl();
  return useMemo<ColDef<BlotterCashEntry>[]>(
    () => [
      {
        field: 'id',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.id' }),
        width: 150,
        lockPosition: true,
        ...textFilterDefinition,
      },
      {
        colId: 'actions',
        valueGetter: ({ data }) => data?.id,
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.actions' }),
        cellRenderer: CommandsCellRenderer,
        width: 100,
        filter: false,
        sortable: false,
      },
      {
        field: 'account',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.account' }),
        width: 200,
        ...textFilterDefinition,
      },
      {
        field: 'currencyPair',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.currencyPair' }),
        width: 100,
        ...textFilterDefinition, // text filtering is more appropriate here rather than a set filter (possibly lots of different currency pairs in the grid)
      },
      {
        colId: 'tradeDate',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.tradeDate' }),
        valueGetter: ({ data }) => data?.execTime,
        cellRenderer: DateRenderer,
        width: 120,
        ...dateFilterDefinition,
      },
      {
        field: 'farDate',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.farDate' }),
        cellRenderer: DateRenderer,
        width: 120,
        ...dateFilterDefinition,
      },
      {
        field: 'farRate',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.farRate' }),
        cellRenderer: RateRenderer,
        width: 120,
      },
      {
        field: 'nearDate',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.nearDate' }),
        cellRenderer: DateRenderer,
        width: 120,
        ...dateFilterDefinition,
      },
      {
        field: 'nearRate',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.nearRate' }),
        cellRenderer: RateRenderer,
        width: 120,
      },
      {
        field: 'dealtAmount',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.dealtAmount' }),
        cellRenderer: AmountRenderer,
        width: 120,
        ...numberFilterDefinition,
      },
      {
        field: 'farDealtAmount',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.farDealtAmount' }),
        cellRenderer: AmountRenderer,
        width: 120,
        ...numberFilterDefinition,
      },
      {
        field: 'dealtCurrency',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.dealtCurrency' }),
        width: 100,
        ...textFilterDefinition, // text filtering is more appropriate here rather than a set filter (possibly lots of different currencies in the grid)
      },
      {
        field: 'contraAmount',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.contraAmount' }),
        cellRenderer: AmountRenderer,
        width: 120,
        ...numberFilterDefinition,
      },
      {
        field: 'farContraAmount',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.farContraAmount' }),
        cellRenderer: AmountRenderer,
        width: 120,
        ...numberFilterDefinition,
      },
      {
        field: 'contraCurrency',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.contraCurrency' }),
        width: 100,
        ...textFilterDefinition, // text filtering is more appropriate here rather than a set filter (possibly lots of different currencies in the grid)
      },
      {
        field: 'points',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.points' }),
        cellRenderer: PointsRenderer,
        width: 80,
        ...numberFilterDefinition,
      },
      {
        field: 'portfolio',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.portfolio' }),
        width: 150,
        ...textFilterDefinition,
      },
      {
        field: 'product',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.product' }),
        width: 100,
        ...setFilterDefinition,
      },
      {
        field: 'sales',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.sales' }),
        width: 250,
        ...textFilterDefinition,
      },
      {
        field: 'spot',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.spot' }),
        cellRenderer: RateRenderer,
        width: 120,
      },
      {
        field: 'spotDate',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.spotDate' }),
        cellRenderer: DateRenderer,
        width: 130,
        ...dateFilterDefinition,
      },
      {
        field: 'status',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.status' }),
        cellRenderer: StatusCellRenderer,
        width: 130,
        ...setFilterDefinition,
      },
      {
        field: 'execTime',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.execTime' }),
        valueGetter: ({ data }) => data?.execTime,
        valueFormatter: ({ value }: { value: ISODateTime }) => basicTimeFormat(value),
        width: 100,
        // no filter here : displayed times are not comparable because they (most probably) belong to different dates
      },
      {
        field: 'venue',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.venue' }),
        width: 150,
        ...setFilterDefinition, // unsure, maybe text filter here, depending on the potential size of the set
      },
      {
        field: 'way',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.way' }),
        width: 150,
        ...setFilterDefinition,
      },
      {
        field: 'dealInitiator',
        headerName: formatMessage({ id: 'blotter.tab.Cash.column.dealInitiator' }),
        width: 250,
        ...textFilterDefinition,
      },
    ],
    [formatMessage],
  );
}
